import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { neutral, palette } from '@ahora-patagonia/design-tokens';
import { IconoPlay } from '../../../../shared/components/iconos.js';

export interface VideoDiferidoProps {
  videoId: string;
  caption: string | null | undefined;
}

/**
 * Video con carga diferida: **no se carga el reproductor de terceros hasta que se toca**.
 * Antes es una imagen con el alto reservado en 16:9 y un botón de 64 px — ahorra entre
 * 400 KB y 1 MB por nota en la primera pintura (handoff §1.3).
 *
 * El estado inicial (`sinCargar`) es idéntico en server y cliente: el iframe no existe en
 * el HTML del SSR ni en el primer render, así que no hay mismatch posible.
 */
export function VideoDiferido({ videoId, caption }: VideoDiferidoProps) {
  const [cargado, setCargado] = useState(false);

  return (
    <Box component="figure" sx={{ m: 0, my: 3 }}>
      <Box sx={{ aspectRatio: '16 / 9', backgroundColor: palette.ink, position: 'relative' }}>
        {cargado ? (
          <iframe
            title={caption ?? 'Video'}
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            style={{ border: 0, width: '100%', height: '100%', display: 'block' }}
          />
        ) : (
          <Box
            component="button"
            type="button"
            aria-label={`Reproducir video${caption ? `: ${caption}` : ''}`}
            onClick={() => setCargado(true)}
            sx={{
              all: 'unset',
              cursor: 'pointer',
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
              alt=""
              loading="lazy"
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <Box
              sx={{
                position: 'relative',
                width: 64,
                height: 64,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(32,30,29,.8)',
                color: '#fff',
              }}
            >
              <IconoPlay size={28} />
            </Box>
          </Box>
        )}
      </Box>
      {caption && (
        <Typography
          component="figcaption"
          variant="epigrafe"
          sx={{ color: neutral[700], mt: 0.5 }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
}
